import { useLayoutEffect, useRef, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled, { useTheme } from "styled-components/macro";
import { match } from "ts-pattern";
import { Controller } from "react-hook-form";
import type { Control } from "react-hook-form";
import { useAuthContext } from "../../../../components/Auth";
import {
  DocumentIcon,
  ImageIcon,
  LinkIcon,
  MagicDocIcon,
  VideoIcon,
} from "../../../../components/Icons/Icons";
import { RadioButton } from "../../../../components/RadioButton/RadioButton";
import {
  TextBaseSemibold,
  TextSmRegular,
} from "../../../../components/Typography/TypographyV2";
import type {
  AccessibilityData,
  SupportedAssetAccessibility,
  Assets,
  SupportedAssetType,
} from "../../../../types/types.PIM";
import {
  GetDocumentIcon,
  TrimmedName,
  get_content_type,
} from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import type { AnalyticsProp } from "../../../../types/types";
import { strings } from "../../../../util/strings";
import { zodOptionalString, zodSelectBoxType } from "../../../../util/zod.util";
import * as z from "zod";
import { useStoreState } from "../../../../util/util";

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  overflow: hidden;
`;

const LinkAnchor = styled.a`
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme }) => theme.primaryTextColor};
  &:hover {
    text-decoration: underline;
  }
`;

export const DocPortfolioName = styled.span<{ disabled: boolean }>`
  color: ${({ theme, disabled }) =>
    disabled ? theme.disabledButtonTextColor : theme.primaryTextColor};
  overflow: hidden;
`;

export const TableContainer = styled.div<{ lastChild?: boolean }>`
  & div[class*="TableWrapper"] {
    overflow: visible;
    td {
      overflow: visible;
    }
  }
  border-bottom: ${({ theme, lastChild }) =>
    lastChild
      ? "none"
      : `${theme.resolve("border_md")} solid ${theme.secondaryBorder}`};
`;

export const ACCESSIBILITY_TYPES = {
  PUBLIC: "public",
  LOGGED_IN_USER: "logged_in_user",
  INTERNAL: "internal",
} as const;

export const AssetNameIcon = ({
  idx,
  asset_type,
  name,
  is_downloadable,
  is_generated,
  is_external_asset,
  content_type,
  set_show_preview,
  set_selected_asset_idx,
  disableTrim,
  url,
}: {
  idx: number;
  asset_type: SupportedAssetType;
  name: string;
  is_downloadable: boolean;
  is_generated: boolean;
  is_external_asset: boolean;
  content_type: string | null;
  set_show_preview: (show: boolean) => void;
  set_selected_asset_idx: (idx: number) => void;
  disableTrim?: boolean;
  url?: string;
}) => {
  const { t } = useTranslation();
  const isDisabled = !is_downloadable;
  return (
    <FlexWrapper>
      <div>
        {is_external_asset ? (
          <LinkIcon />
        ) : (
          match(asset_type)
            .with("image", () => <ImageIcon />)
            .with("document", () =>
              is_generated ? (
                <div
                  data-tip={t("Generated Document")}
                  data-for="generated-doc-tooltip"
                >
                  <MagicDocIcon />
                  <ReactTooltip effect="solid" id="generated-doc-tooltip" />
                </div>
              ) : (
                <GetDocumentIcon
                  asset_type={asset_type}
                  file_type={get_content_type(
                    content_type ?? "application/pdf"
                  )}
                />
              )
            )
            .with("video", () => <VideoIcon />)
            .otherwise(() => <DocumentIcon />)
        )}
      </div>
      {isDisabled ? (
        <>
          <DocPortfolioName
            data-tip={match(asset_type)
              .with("image", () => t("Register to access this image"))
              .with("document", () => t("Register to access this document"))
              .with("video", () => t("Register to access this video"))
              .otherwise(() => (
                <DocumentIcon />
              ))}
            data-for={`${name}-doc-tooltip`}
            disabled={true}
          >
            {name}
          </DocPortfolioName>
          <ReactTooltip effect="solid" id={`${name}-doc-tooltip`} />
        </>
      ) : (
        <div
          style={{
            width: "100%",
            cursor: "pointer",
            overflow: "hidden",
          }}
          onClick={(e) => {
            e.preventDefault();
            set_selected_asset_idx(idx >= 0 ? idx : 0);
            is_external_asset && !!url
              ? window.open(url, "_blank")
              : set_show_preview(true);
          }}
        >
          {disableTrim ? <span>{name}</span> : <TrimmedName text={name} />}
        </div>
      )}
    </FlexWrapper>
  );
};

export const AssetLink = ({
  url,
  name,
  is_visible,
  analytics,
}: {
  url: string;
  name: string;
  is_visible: boolean;
  analytics?: AnalyticsProp;
}) => {
  const { roleIsGuest } = useAuthContext();
  const { t } = useTranslation();
  const isDisabled = roleIsGuest && !is_visible;
  return (
    <div>
      <FlexWrapper>
        <LinkIcon />
        {isDisabled ? (
          <>
            <DocPortfolioName
              data-tip={t("Register to access this link")}
              data-for={`${name}-link-tooltip`}
              disabled={true}
            >
              {name}
            </DocPortfolioName>
            <ReactTooltip effect="solid" id={`${name}-link-tooltip`} />
          </>
        ) : (
          <LinkAnchor href={url} target="_blank" rel="noopener noreferrer">
            <TrimmedName analytics={analytics} text={name} />
          </LinkAnchor>
        )}
      </FlexWrapper>
    </div>
  );
};

const processAccessibility = (
  allowAllFlag: string,
  options?: string[] | null
) => {
  const hasAllowAll =
    (allowAllFlag !== "" && options?.includes(allowAllFlag)) || false;
  return {
    processedIds: hasAllowAll
      ? []
      : options?.filter((id) => !id.includes("allow_all")) ?? [],
    allowAll: hasAllowAll,
  };
};

export const getUsersExistingData = (users?: AccessibilityData[] | null) =>
  (users || []).map((u) => ({
    id: u.id,
    name: u.firstname + " " + u.lastname,
  }));

export const getRolesExistingData = (
  allowAllRoles?: boolean,
  roles?: AccessibilityData[] | null
) =>
  allowAllRoles
    ? [{ id: "allow_all_roles", name: "All Roles" }]
    : (roles || []).map((r) => ({ id: r.id, name: r.name ?? "" }));

export const getCustomersExistingData = (
  allowAllCustomers?: boolean,
  allowAllDistributors?: boolean,
  customers?: AccessibilityData[] | null
) => {
  const result = [];
  if (allowAllCustomers) {
    result.push({ id: "allow_all_customers", name: "All Customers" });
  }
  if (allowAllDistributors) {
    result.push({ id: "allow_all_distributors", name: "All Distributors" });
  }
  const customerData = (customers || []).map((c) => ({
    id: c.id,
    name: c.name ?? "",
  }));
  result.push(...customerData);
  return result;
};

export const accessibilityRequestData = (
  asset_type: SupportedAssetType,
  accessibility: string,
  customer_ids?: string[] | null,
  user_ids?: string[] | null,
  role_ids?: string[] | null
) => {
  if (asset_type !== "document") {
    return {
      allow_all_customers: accessibility !== ACCESSIBILITY_TYPES.INTERNAL,
      allow_all_distributors: accessibility !== ACCESSIBILITY_TYPES.INTERNAL,
      allow_all_roles: true,
    };
  }
  const customerAccessibility = processAccessibility(
    "allow_all_customers",
    customer_ids
  );
  const distributorAccessibility = processAccessibility(
    "allow_all_distributors",
    customer_ids
  );
  const userAccessibility = processAccessibility("", user_ids);
  const roleAccessibility = processAccessibility("allow_all_roles", role_ids);
  return {
    allow_all_customers:
      accessibility === ACCESSIBILITY_TYPES.LOGGED_IN_USER &&
      customerAccessibility.allowAll,
    allow_all_distributors:
      accessibility === ACCESSIBILITY_TYPES.LOGGED_IN_USER &&
      distributorAccessibility.allowAll,
    allow_all_roles: roleAccessibility.allowAll,
    customer_ids:
      accessibility === ACCESSIBILITY_TYPES.PUBLIC ||
      accessibility === ACCESSIBILITY_TYPES.INTERNAL
        ? []
        : customerAccessibility.processedIds.concat(
            distributorAccessibility.processedIds
          ),
    user_ids:
      accessibility === ACCESSIBILITY_TYPES.PUBLIC
        ? []
        : userAccessibility.processedIds,
    role_ids:
      accessibility === ACCESSIBILITY_TYPES.PUBLIC
        ? []
        : roleAccessibility.processedIds,
  };
};

export const disableAccessibilitySubmit = (
  asset_type: SupportedAssetType,
  accessibility: string,
  input_role_ids?: string[],
  input_user_ids?: string[]
) => {
  return (
    asset_type === "document" &&
    accessibility !== ACCESSIBILITY_TYPES.PUBLIC &&
    input_role_ids?.length === 0 &&
    input_user_ids?.length === 0
  );
};

// ---------------------------------------------------------------------------
// Shared asset-form styled components
// ---------------------------------------------------------------------------

export const AddFromSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

export const AddFromHeadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
`;

export const RadioOptionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

// ---------------------------------------------------------------------------
// Shared asset-form components
// ---------------------------------------------------------------------------

type AssetSourceModeValue = Assets["asset_mode"];

/**
 * The "Add from" section with Upload / External radio options.
 * Wrap with `enable_external_assets` guard in the parent form.
 */
export const AssetSourceModeController = ({
  control,
  register,
  is_edit_mode = false,
}: {
  control: Control<any>;
  register: any;
  is_edit_mode?: boolean;
}) => {
  const { t } = useTranslation();
  const {
    storefront_metadata: { enable_external_assets },
  } = useStoreState();
  const theme = useTheme();

  return enable_external_assets ? (
    <AddFromSection>
      <AddFromHeadingWrapper>
        <TextBaseSemibold>{t("Add from")}</TextBaseSemibold>
        <TextSmRegular color="secondary">
          {t("Select where to add the asset from")}
        </TextSmRegular>
      </AddFromHeadingWrapper>
      <Controller
        control={control}
        name="asset_source_mode"
        render={({ onChange, value }) => (
          <RadioOptionsStack>
            <RadioButton
              name="asset_source_mode"
              value="gcs"
              checked={value !== "external"}
              optionTitle={t("Upload new asset")}
              fontSize={theme.resolve("font_size_md")}
              color={
                value !== "external"
                  ? theme.resolve("color_text_primary")
                  : theme.resolve("color_text_secondary")
              }
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onChange(event.currentTarget.value as AssetSourceModeValue);
              }}
              disabled={is_edit_mode}
            />
            <RadioButton
              name="asset_source_mode"
              value="external"
              checked={value === "external"}
              optionTitle={t("Link externally hosted asset")}
              fontSize={theme.resolve("font_size_md")}
              color={
                value === "external"
                  ? theme.resolve("color_text_primary")
                  : theme.resolve("color_text_secondary")
              }
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onChange(event.currentTarget.value as AssetSourceModeValue);
              }}
              disabled={is_edit_mode}
            />
          </RadioOptionsStack>
        )}
      />
    </AddFromSection>
  ) : (
    <input
      type="hidden"
      name="asset_source_mode"
      value="gcs"
      ref={register()}
    />
  );
};

/** Standardised "Asset Metadata" section heading used in asset forms. */
export const AssetMetadataHeading = () => {
  const { t } = useTranslation();
  return (
    <AddFromHeadingWrapper>
      <TextBaseSemibold>{t("Asset Metadata")}</TextBaseSemibold>
      <TextSmRegular color="secondary">
        {t("Add metadata for this asset")}
      </TextSmRegular>
    </AddFromHeadingWrapper>
  );
};

// ---------------------------------------------------------------------------
// Shared asset-form utilities
// ---------------------------------------------------------------------------

/** Derives the asset display name from the active source mode. */
export const getAssetName = (
  assetSourceMode: AssetSourceModeValue,
  assetName?: string,
  externalAsset?: { display_text?: string } | null
): string =>
  assetSourceMode === "external"
    ? externalAsset?.display_text?.trim() ?? ""
    : assetName?.trim() ?? "";

// ---------------------------------------------------------------------------
// Shared zod sub-schemas for asset source mode / external asset
// ---------------------------------------------------------------------------

export const assetSourceModeSchema = z.enum(["gcs", "external", "cache"]);

/** Type-inference shape (no validation messages). */
export const externalAssetInferenceSchema = z
  .object({
    display_text: z.string(),
    url: z.string(),
  })
  .optional()
  .nullable();

/** Validation shape (requires non-empty strings). */
export const externalAssetValidationSchema = (t: TFunction) =>
  z
    .object({
      display_text: z
        .string()
        .min(1, { message: strings(t).thisIsARequiredField }),
      url: z.string().min(1, { message: strings(t).thisIsARequiredField }),
    })
    .optional()
    .nullable();

export const assetMetadataLabelMap = (
  t: TFunction
): Record<string, string> => ({
  name: t("Asset Name"),
  asset_type: t("Asset Type"),
  asset_category: t("Category"),
  host: t("Asset Host"),
  language: t("Language"),
  document_type: t("Document Type"),
  asset_host: t("Asset Host"),
  issue_date: t("Issue Date"),
  expiration_date: t("Expiration Date"),
  renewal_date: t("Renewal Date"),
  country: t("Country"),
  region: t("Region"),
});

export const AddAssetSchema = z.object({
  asset_name: z.string().optional(),
  asset_source_mode: assetSourceModeSchema,
  asset_host: zodSelectBoxType,
  file_type: z.instanceof(File).optional(),
  external_asset: z
    .object({
      display_text: z.string(),
      url: z.string(),
    })
    .optional(),
  input_asset_type: zodSelectBoxType,
  input_category_type: zodSelectBoxType,
  input_lang_type: zodSelectBoxType,
  input_document_type: zodSelectBoxType.optional().nullable(),
  issue_date: zodOptionalString,
  expiration_date: zodOptionalString,
  renewal_date: zodOptionalString,
  country: zodSelectBoxType.optional().nullable(),
  region: zodSelectBoxType.optional().nullable(),
  product_id: zodSelectBoxType.optional(),
  custom_category_name: zodOptionalString,
  visibility: z.string(),
  input_customer_ids: z.array(z.string()).optional().nullable(),
  input_user_ids: z.array(z.string()).optional().nullable(),
  input_role_ids: z.array(z.string()).optional().nullable(),
});

export type NewAssetFormValues = z.infer<typeof AddAssetSchema>;

export type NewAssetFormSubmitValues = NewAssetFormValues & {
  accessibility?: SupportedAssetAccessibility;
  selected_file?: File | null;
};

/**
 * Detects whether the form is rendered inside a slideout overlay and returns
 * the ref to attach to the form element along with the boolean result.
 * Re-runs whenever `assetSourceMode` changes (mode switches cause a DOM
 * re-render that may move the form in/out of the slideout).
 */
export const useIsInSlideout = (assetSourceMode: string) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [isInSlideout, setIsInSlideout] = useState(false);

  useLayoutEffect(() => {
    setIsInSlideout(
      !!formRef.current?.closest('[data-testid="slideout-overlay"]')
    );
  }, [assetSourceMode, formRef]);

  return { formRef, isInSlideout };
};
